import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useStepContext } from '../contexts/stepContext';
import { useSubscriptionContext } from '../contexts/subscriptionContext';
import { useAppSelector, useLocaleCurrencyFormat, useTrack } from '../hooks';
import { debounce } from '../utils/debounce';
import { requestChangeProduct } from '../smi-bridge';
import { LocaleText, useMakeLocalizedText } from '../LocaleText';
import { Footer } from '../components/Footer';
import { ClearIcon, SearchIcon } from '../components/icons';
import type { SkuSwapNode } from '../types';

export const SkuSwapStep = ({
  // step unused for now, but declared for consistency with other steps
  step: _step
}: {
  step: SkuSwapNode;
}) => {
  const { dispatch } = useStepContext();
  const subscription = useSubscriptionContext();

  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [heightLocked, setHeightLocked] = useState(false);

  const track = useTrack();
  const currencyFormat = useLocaleCurrencyFormat();
  const makeLocalizedText = useMakeLocalizedText();

  // Get alternative products
  const alternativeProductIds = useAppSelector(state => state.sku_swap.subscriptions?.[subscription.public_id]);
  const productById = useAppSelector(state => state.product_by_id);
  const alternativeProducts = useMemo(() => {
    if (!alternativeProductIds || alternativeProductIds.length === 0) {
      return [];
    }
    return alternativeProductIds.map((id: string) => productById[id]).filter(Boolean);
  }, [alternativeProductIds, productById]);

  // Get order and locale for currency formatting
  const orderItem = useAppSelector(state => state.order_items.find(oi => oi.subscription === subscription.public_id));
  const order = useAppSelector(state => state.orders.find(o => o.public_id === orderItem?.order));
  const currencyCode = order?.currency_code || 'USD';

  const itemsContainerRef = useRef<HTMLDivElement>(null);

  // Debounced search value update
  const updateDebouncedSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearchValue(value);

      // Auto-deselect if selected product becomes hidden
      if (selectedProductId && value) {
        const query = value.toLowerCase();
        const filtered = alternativeProducts.filter(product => product.name.toLowerCase().includes(query));
        const isSelectedVisible = filtered.some(p => p.external_product_id === selectedProductId);
        if (!isSelectedVisible) {
          setSelectedProductId(null);
        }
      }
    }, 150),
    [selectedProductId, alternativeProducts]
  );

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchValue(value); // Immediate update for input

    // Height locking on first search
    if (value && !heightLocked && itemsContainerRef.current) {
      const currentHeight = itemsContainerRef.current.offsetHeight;
      itemsContainerRef.current.style.height = `${currentHeight}px`;
      setHeightLocked(true);
    }

    updateDebouncedSearch(value); // Debounced update for filtering
  };

  // Filtered products based on debounced search
  const filteredProducts = useMemo(() => {
    if (!debouncedSearchValue) return alternativeProducts;
    const query = debouncedSearchValue.toLowerCase();
    return alternativeProducts.filter(product => product.name.toLowerCase().includes(query));
  }, [debouncedSearchValue, alternativeProducts]);

  // Track shown event on mount
  useEffect(() => {
    track('subscriptioncancel_skuswap_shown', {
      subscriptionId: subscription.public_id,
      productIdFrom: subscription.product
    });
  }, [subscription.public_id]);

  // Handle swap
  async function handleSwap() {
    if (!selectedProductId) return;

    try {
      setIsSubmitting(true);
      await requestChangeProduct({
        product_id: selectedProductId,
        subscription_id: subscription.public_id
      });
      track('subscriptioncancel_completed_saved_swapped', {
        subscriptionId: subscription.public_id,
        productIdTo: selectedProductId,
        productIdFrom: subscription.product
      });
      dispatch({ type: 'accepted' });
    } catch (error) {
      console.error('Error changing product:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="og-sku-swap-product-change">
      <div className="og-dialog-body">
        <p>
          <LocaleText t="subheader" />
        </p>

        {/* Prepaid note */}
        {(subscription.prepaid_subscription_context?.prepaid_orders_remaining ?? 0) > 0 && (
          <p className="og-prepaid-note">
            <LocaleText t="prepaidNote" />
          </p>
        )}

        {/* Search input */}
        <div className="og-sku-swap-search-wrapper">
          <input
            type="text"
            placeholder={makeLocalizedText('searchPlaceholder')}
            aria-label={makeLocalizedText('searchLabel')}
            value={searchValue}
            onChange={e => handleSearchChange(e.target.value)}
          />
          <div className="og-sku-swap-search-icons">
            <button
              className="og-icon-button"
              type="button"
              aria-label={makeLocalizedText('clearSearch')}
              onClick={() => handleSearchChange('')}
            >
              <ClearIcon />
            </button>
            <div className="og-sku-swap-search-icon">
              <SearchIcon />
            </div>
          </div>
        </div>

        {/* Product grid */}
        <div className="og-sku-swap-display" ref={itemsContainerRef}>
          {/* No results message */}
          <div className="og-no-result-message" style={{ display: filteredProducts.length === 0 ? 'block' : 'none' }}>
            <LocaleText t="noResults" />
          </div>

          {/* Product items */}
          {filteredProducts.map(product => (
            <React.Fragment key={`product-${product.external_product_id}`}>
              <input
                type="radio"
                name="sku-swap-product"
                id={`product-${product.external_product_id}`}
                value={product.external_product_id}
                checked={selectedProductId === product.external_product_id}
                onChange={() => setSelectedProductId(product.external_product_id)}
                className="visually-hidden-input"
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor={`product-${product.external_product_id}`}>
                <div className="flex-row og-order-item-info text-sm">
                  <div className="og-thumbnail">
                    {product.image_url && (
                      <img src={product.image_url} alt="" onError={e => (e.currentTarget.style.display = 'none')} />
                    )}
                  </div>
                  <div className="flex-column justify-content-center overflow-ellipsis">
                    <span className="text-sm fw-bold">{product.name}</span>
                    <span className="final-price">{currencyFormat(product.price, currencyCode)}</span>
                  </div>
                </div>
              </label>
            </React.Fragment>
          ))}
        </div>
      </div>

      <Footer>
        <button
          disabled={isSubmitting || !selectedProductId}
          onClick={handleSwap}
          className="og-button"
          data-variant="primary"
        >
          <LocaleText t="swapButton" />
        </button>
      </Footer>
    </div>
  );
};
